import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { ChevronRight, Info } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"

/**
 * Was kostet uns ein Kunde? — Grundlage fuer die interne Preisgestaltung.
 *
 * Die Seite beantwortet EINE Frage und ordnet alles danach: was kostet ein
 * Kunde im Monat, und wovon. Deshalb:
 *
 *  - **Euro, nicht Dollar.** Drei von vier Rechnungen kommen in Euro, und in
 *    Euro wird kalkuliert. Bedrock rechnet in Dollar; der Kurs steht unten.
 *  - **Zwei Zahlen je Kunde, nicht eine.** „Verursacht" ist das, was der Kunde
 *    ausgeloest hat (KI-Aufrufe, seine Gespraeche). „Umlage" faellt ohnehin an
 *    und wird geteilt (Server, Grundgebuehr). Nur zusammengezaehlt beantwortet
 *    es keine der beiden Fragen — beim Verhandeln zaehlt die erste.
 *  - **Der Eigenaufwand steht unten**, nicht zwischen den Kunden.
 */

type Posten = {
  zweck: string
  label: string
  usd: number | null
  aufrufe: number
  modelle: string[]
  umlage?: boolean
}

type Provider = {
  provider: string
  label: string
  usd: number | null
  aufrufe: number
  offen?: boolean
  umlage?: boolean
  hinweis?: string
  posten: Posten[]
}

type Eintrag = {
  umgebung: string
  periode: string
  tenant: string
  tenant_name: string
  ist_test: boolean
  usd_gesamt: number
  usd_direkt: number
  usd_umlage: number
  aufrufe: number
  mails: number
  anrufe: number
  provider: Provider[]
}

type Umgebung = { umgebung: string; label: string; erreichbar: boolean; fehler: string | null }
type Summe = { periode: string; usd_gesamt: number; je_umgebung: Record<string, number> }

/** Azure-Rechnung gegen die Summe der Traces (Paul 23.10.2026). USD wie alles. */
type AzureAbgleich = {
  monat: string
  rechnung_eur: number
  rechnung_herkunft: string
  zugeordnet_usd: number
  je_posten_usd: Record<string, number>
  rest_usd: number
  ueberschritten: boolean
}

type Antwort = {
  items: Eintrag[]
  azure_abgleich?: AzureAbgleich[]
  umgebungen: Umgebung[]
  umgebung_labels: Record<string, string>
  summen: Summe[]
  granularitaet: string
  aws_aufschlag: number
  eur_usd: number
  offene_provider: string[]
  hinweise: string[]
}

async function fetchKosten(anzahl: number, granular: string): Promise<Antwort> {
  const r = await fetch(`/api/v1/costs?monate=${anzahl}&granularitaet=${granular}`,
                        { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli",
                "August", "September", "Oktober", "November", "Dezember"]

/** „2026-08" → „August 2026", „2026-08-19" → „Mi, 19. August". */
function periodeLabel(p: string): string {
  const [j, mo, tag] = p.split("-")
  const monat = MONATE[Number(mo) - 1] || mo
  if (!tag) return `${monat} ${j}`
  const wt = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][
    new Date(Number(j), Number(mo) - 1, Number(tag)).getDay()]
  return `${wt}, ${Number(tag)}. ${monat}`
}

const F = {
  seite: { color: "#2C2C2A" } as const,
  karte: {
    border: "1px solid #EAE9E4", borderRadius: 10, padding: "14px 16px",
    background: "#fff",
  } as const,
  klein: { fontSize: 12, color: "#8A8880" } as const,
  zeile: {
    display: "flex", alignItems: "center", gap: 10, width: "100%",
    padding: "9px 4px", background: "none", border: "none", cursor: "pointer",
    textAlign: "left" as const, fontSize: 13,
  } as const,
}

export default function KostenPage() {
  const [anzahl, setAnzahl] = useState(6)
  const [granular, setGranular] = useState("monat")
  const [offen, setOffen] = useState<Record<string, boolean>>({})
  const [eigenaufwand, setEigenaufwand] = useState(false)

  const q = useQuery({
    queryKey: ["kosten", anzahl, granular],
    queryFn: () => fetchKosten(anzahl, granular),
    staleTime: 60_000,
  })

  if (q.isLoading) return <Loading />
  if (q.error) return <ErrorBox error={q.error} />

  const kurs = q.data?.eur_usd || 1.09
  /** Alles kommt in USD aus der API — angezeigt wird Euro. */
  const eur = (v: number | null | undefined) =>
    v === null || v === undefined ? "—" : `${(v / kurs).toFixed(2)} €`
  const cent = (v: number | null | undefined) =>
    v === null || v === undefined ? "—" : `${(v / kurs * 100).toFixed(1)} ct`

  const items = q.data?.items || []
  const summen: Record<string, Summe> = {}
  for (const s of q.data?.summen || []) summen[s.periode] = s
  const kaputt = (q.data?.umgebungen || []).filter((u) => !u.erreichbar)
  const abgleich: Record<string, AzureAbgleich> = {}
  for (const a of q.data?.azure_abgleich || []) abgleich[a.monat] = a

  const perioden = [...new Set(items.map((e) => e.periode))].sort().reverse()
  const kunden = (p: string) =>
    items.filter((e) => e.periode === p && e.umgebung === "prod"
                       && e.tenant !== "(gemeinsam)" && !e.ist_test)
         .sort((a, b) => b.usd_gesamt - a.usd_gesamt)
  const eigen = (p: string) =>
    items.filter((e) => e.periode === p
                       && (e.umgebung === "dev" || e.tenant === "(gemeinsam)" || e.ist_test))
         .sort((a, b) => b.usd_gesamt - a.usd_gesamt)

  return (
    <div style={F.seite}>
      <PageHeader title="Kosten" onRefresh={() => q.refetch()} refreshing={q.isFetching}>
        <select className="pp-select pp-select-sm" value={granular}
          onChange={(e) => { setGranular(e.target.value); setAnzahl(e.target.value === "tag" ? 30 : 6) }}>
          <option value="monat">Monatsweise</option>
          <option value="tag">Tagesweise</option>
        </select>
        <select className="pp-select pp-select-sm" value={anzahl}
          onChange={(e) => setAnzahl(Number(e.target.value))}>
          {granular === "tag"
            ? [7, 14, 30, 92].map((n) => <option key={n} value={n}>{n} Tage</option>)
            : [3, 6, 12].map((n) => <option key={n} value={n}>{n} Monate</option>)}
        </select>
      </PageHeader>

      {kaputt.length > 0 && (
        <div style={{ ...F.karte, background: "#FDECEA", borderColor: "#F5C6C0", marginBottom: 14 }}>
          <strong>{kaputt.map((u) => u.label).join(", ")} konnte nicht gelesen werden.</strong>{" "}
          Die Summen sind deshalb zu niedrig — das ist ein Ausfall, keine Ersparnis.
          {kaputt.map((u) => (
            <div key={u.umgebung} style={{ ...F.klein, fontFamily: "monospace", marginTop: 4 }}>{u.fehler}</div>
          ))}
        </div>
      )}

      {perioden.map((periode) => {
        const liste = kunden(periode)
        const eigenListe = eigen(periode)
        const summeKunden = liste.reduce((s, e) => s + e.usd_gesamt, 0)
        const summeEigen = eigenListe.reduce((s, e) => s + e.usd_gesamt, 0)
        const az = granular === "monat" ? abgleich[periode] : undefined
        const azVoice = az ? Object.entries(az.je_posten_usd)
          .filter(([k]) => k.startsWith("voice_")).reduce((s, [, v]) => s + v, 0) : 0

        return (
          <div key={periode} style={{ marginBottom: 30 }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "baseline",
              borderBottom: "2px solid #EAE9E4", paddingBottom: 6, marginBottom: 12,
            }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{periodeLabel(periode)}</h3>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {eur(summeKunden + summeEigen)}
              </span>
            </div>

            {/* Je Kunde eine Karte: die Zahl, die beim Verhandeln zaehlt, und
                daneben was ohnehin anfaellt. */}
            {liste.map((e) => {
              const key = `${e.periode}:${e.tenant}`
              const auf = !!offen[key]
              return (
                <div key={key} style={{ ...F.karte, marginBottom: 8, padding: "10px 14px" }}>
                  <button onClick={() => setOffen((o) => ({ ...o, [key]: !o[key] }))} style={F.zeile}>
                    <ChevronRight className="w-3 h-3" style={{
                      transform: auf ? "rotate(90deg)" : "none", transition: "transform .15s",
                      color: "#8A8880", flexShrink: 0,
                    }} />
                    <span style={{ fontWeight: 600, minWidth: 190 }}>{e.tenant_name}</span>
                    <span style={{ ...F.klein, flex: 1 }}>
                      {e.mails > 0 && `${e.mails} Mails`}
                      {e.anrufe > 0 && ` · ${e.anrufe} Anrufe`}
                      {e.mails > 0 && ` · ${cent(e.usd_direkt / e.mails)} je Mail`}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{eur(e.usd_gesamt)}</span>
                      <span style={{ ...F.klein, display: "block", marginTop: 1 }}>
                        {eur(e.usd_direkt)} verursacht + {eur(e.usd_umlage)} Umlage
                      </span>
                    </span>
                  </button>

                  {auf && (
                    <div style={{ paddingLeft: 24, paddingTop: 6 }}>
                      {e.provider.map((p) => (
                        <div key={p.provider} style={{ marginBottom: 10 }}>
                          <div style={{
                            display: "flex", justifyContent: "space-between", fontSize: 12,
                            fontWeight: 500, padding: "4px 0", borderBottom: "1px solid #F0EFEA",
                            color: p.offen ? "#8A8880" : "#2C2C2A",
                          }}>
                            <span>
                              {p.label}
                              {p.offen && (
                                <span style={{
                                  marginLeft: 6, fontSize: 12, background: "#F0EFEA",
                                  color: "#8A8880", padding: "1px 6px", borderRadius: 10,
                                }}>noch nicht angebunden</span>
                              )}
                            </span>
                            <span>{eur(p.usd)}</span>
                          </div>
                          {p.hinweis && (
                            <div style={{ ...F.klein, display: "flex", gap: 6, padding: "6px 0" }}>
                              <Info className="w-3 h-3" style={{ flexShrink: 0, marginTop: 1 }} />
                              <span>{p.hinweis}</span>
                            </div>
                          )}
                          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                            <tbody>
                              {p.posten.map((po) => (
                                <tr key={po.zweck} style={{ color: "#5F5E5A" }}>
                                  <td style={{ padding: "3px 0" }}>
                                    {po.label}
                                    {po.umlage && (
                                      <span style={{
                                        marginLeft: 6, fontSize: 12, background: "#F4F3EE",
                                        color: "#8A8880", padding: "1px 5px", borderRadius: 8,
                                      }}>geteilt</span>
                                    )}
                                  </td>
                                  <td style={{ padding: "3px 8px", textAlign: "right", color: "#8A8880", width: 60 }}>
                                    {po.aufrufe > 0 ? `${po.aufrufe}×` : ""}
                                  </td>
                                  <td style={{ padding: "3px 0", textAlign: "right", width: 70 }}>
                                    {eur(po.usd)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Azure-Abgleich: was die Traces erklaeren und was von der
                Rechnung als „nicht zuordenbar" uebrig bleibt. */}
            {az && (
              <div style={{
                ...F.karte, ...F.klein, marginTop: 8, padding: "8px 14px",
                background: az.ueberschritten ? "#FFF6E5" : "#FAFAF8",
              }}>
                <strong style={{ color: "#5F5E5A" }}>Azure-Abgleich:</strong>{" "}
                Rechnung {az.rechnung_eur.toFixed(2)} € ({az.rechnung_herkunft})
                {" · "}zugeordnet {eur(az.zugeordnet_usd)} (Telefonbot {eur(azVoice)},
                Portal-KI {eur(az.je_posten_usd.portal_ki || 0)})
                {" · "}nicht zuordenbar {eur(az.rest_usd)}
                {az.ueberschritten && (
                  <div style={{ marginTop: 3 }}>
                    Die Traces liegen über dem Rechnungsbetrag — der ist vermutlich nur
                    fortgeschrieben. Echte Azure-Rechnung für den Monat eintragen.
                  </div>
                )}
              </div>
            )}

            {/* Eigenaufwand: dev-Server, Testlaeufe, Aufrufe ohne Kunde. Gehoert
                auf die AWS-Rechnung, aber nicht in die Kundenkalkulation —
                deshalb eingeklappt. */}
            {eigenListe.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <button onClick={() => setEigenaufwand((v) => !v)}
                  style={{ ...F.zeile, ...F.klein, padding: "6px 4px" }}>
                  <ChevronRight className="w-3 h-3" style={{
                    transform: eigenaufwand ? "rotate(90deg)" : "none",
                    transition: "transform .15s", flexShrink: 0,
                  }} />
                  <span style={{ flex: 1 }}>
                    Eigenaufwand (dev-Server, Messläufe, Aufrufe ohne Kunden) — kein Kundenaufwand
                  </span>
                  <span>{eur(summeEigen)}</span>
                </button>
                {eigenaufwand && eigenListe.map((e) => (
                  <div key={`${e.periode}:${e.umgebung}:${e.tenant}`} style={{
                    display: "flex", justifyContent: "space-between", ...F.klein,
                    padding: "3px 0 3px 28px",
                  }}>
                    <span>{e.tenant_name} · {e.umgebung === "dev" ? "dev-Server" : "Produktion"}</span>
                    <span>{eur(e.usd_gesamt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {items.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", ...F.klein, fontSize: 13 }}>
          Keine Kostendaten im gewählten Zeitraum.
        </div>
      )}

      <div style={{ ...F.karte, background: "#FAFAF8", marginTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Wie diese Zahlen entstehen</div>
        <div style={{ ...F.klein, lineHeight: 1.7 }}>
          <div>· <strong>Verursacht</strong> — vom Kunden ausgelöst: KI-Aufrufe (aus den Token-Zahlen
            jedes Aufrufs) und seine Telefonie (aus Dauer und Ziel jedes Gesprächs).</div>
          <div>· <strong>Umlage</strong> — fällt ohnehin an (Server, Sipgate-Grundgebühr, der nicht
            zuordenbare Rest der Azure-Rechnung) und wird nach dem Anteil am verursachten Aufwand
            verteilt: wer die Plattform stärker nutzt, trägt mehr davon.</div>
          <div>· <strong>Telefonbot</strong> — je Anruf aus den Tokens (Gespräch, Zusammenfassung);
            das zweite Ohr ist über die Anrufdauer genähert. Tagesweise sichtbar.</div>
          <div>· Angezeigt in Euro, gerechnet zum Kurs {kurs.toFixed(2)} USD/EUR.
            AWS rechnet in Dollar, die übrigen Rechnungen kommen in Euro.</div>
          {(q.data?.hinweise || []).map((h, i) => <div key={i}>· {h}</div>)}
        </div>
      </div>
    </div>
  )
}
